import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import TokenService from './token'

export interface ApiRequest {
	params?: Map<string, any> | null
	payload?: any
	q?: Map<string, string> | null
	uriKey: string
}

export class ApiService {
	private axiosInstance: AxiosInstance
	private tokenService: TokenService
	static cache: Map<string, any> = new Map<string, any>()
	static routes: Map<string, any> = new Map<string, any>()

	constructor(middleware = false, version = '1', conf?: AxiosRequestConfig) {
		const launchpad: { [key: string]: string } = {
			1: import.meta.env.VITE_API_URL,
			2: import.meta.env.VITE_API_V2_URL
		}

		this.axiosInstance = axios.create({
			baseURL: middleware ? import.meta.env.VITE_MIDDLEWARE_API_URL : launchpad[version],
			...conf
		})

		this.tokenService = new TokenService()

		// Intercept the request
		this.axiosInstance.interceptors.request.use(
			(config) => {
				if (middleware) {
					config.headers.Authorization = `Bearer ${this.tokenService.getMiddlewareToken()}`
				}

				return config
			},
			(error: AxiosError) => {
				// handling error
				console.error(error)
			}
		)
	}

	/**
	 * HTTP GET method, used to fetch data `statusCode`: 200.
	 *
	 * @access public
	 * @template T - `TYPE`: expected object.
	 * @template R - `RESPONSE`: expected object inside a axios response format.
	 * @param {string} url - endpoint you want to reach.
	 * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
	 * @returns {Promise<R>} HTTP `axios` response payload.
	 * @memberof Api
	 */
	public get<T, R = AxiosResponse<T>>(
		url: string,
		config: AxiosRequestConfig = {
			method: 'get'
		}
	): Promise<R> {
		return this.axiosInstance.get(url, config)
	}

	/**
	 * HTTP POST method `statusCode`: 201 Created.
	 *
	 * @access public
	 * @template T - `TYPE`: expected object.
	 * @template B - `BODY`: body request object.
	 * @template R - `RESPONSE`: expected object inside a axios response format.
	 * @param {string} url - endpoint you want to reach.
	 * @param {B} data - payload to be send as the `request body`,
	 * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
	 * @returns {Promise<R>} - HTTP [axios] response payload.
	 * @memberof Api
	 */
	public post<T, B, R = AxiosResponse<T>>(
		url: string,
		data?: B,
		config?: AxiosRequestConfig
	): Promise<R> {
		return this.axiosInstance.post(url, data, config)
	}

	/**
	 * HTTP PUT method.
	 *
	 * @access public
	 * @template T - `TYPE`: expected object.
	 * @template B - `BODY`: body request object.
	 * @template R - `RESPONSE`: expected object inside a axios response format.
	 * @param {string} url - endpoint you want to reach.
	 * @param {B} data - payload to be send as the `request body`,
	 * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
	 * @returns {Promise<R>} - HTTP [axios] response payload.
	 * @memberof Api
	 */
	public put<T, B, R = AxiosResponse<T>>(
		url: string,
		data?: B,
		config?: AxiosRequestConfig
	): Promise<R> {
		return this.axiosInstance.put(url, data, config)
	}

	/**
	 * HTTP PATCH method.
	 *
	 * @access public
	 * @template T - `TYPE`: expected object.
	 * @template B - `BODY`: body request object.
	 * @template R - `RESPONSE`: expected object inside a axios response format.
	 * @param {string} url - endpoint you want to reach.
	 * @param {B} data - payload to be send as the `request body`,
	 * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
	 * @returns {Promise<R>} - HTTP [axios] response payload.
	 * @memberof Api
	 */
	public patch<T, B, R = AxiosResponse<T>>(
		url: string,
		data?: B,
		config?: AxiosRequestConfig
	): Promise<R> {
		return this.axiosInstance.patch(url, data, config)
	}

	/**
	 * HTTP DELETE method.
	 *
	 * @access public
	 * @template T - `TYPE`: expected object.
	 * @template R - `RESPONSE`: expected object inside a axios response format.
	 * @param {string} url - endpoint you want to reach.
	 * @param {import("axios").AxiosRequestConfig} [config] - axios request configuration.
	 * @returns {Promise<R>} - HTTP [axios] response payload.
	 * @memberof Api
	 */
	public delete<T, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
		return this.axiosInstance.delete(url, config)
	}

	/**
	 * Gets Token.
	 *
	 * @returns {string} token.
	 * @memberof Api
	 */
	public getToken(): string {
		return `Bearer ${this.tokenService.getIdToken()}`
	}

	/**
	 * Get Uri
	 *
	 * @param {import("axios").AxiosRequestConfig} [config]
	 * @returns {string}
	 * @memberof Api
	 */
	public getUri(config?: AxiosRequestConfig): string {
		return this.getUri(config)
	}

	public makeRoute(request: ApiRequest): string {
		let route = ApiService.routes.get(request.uriKey)

		if (request.params !== null) {
			const routeParts: Array<string> = route.split('/')

			const routeUrlParts: Array<string> = []
			let param = ''

			routeParts.forEach((part) => {
				if (part[0] === '{') {
					param = part.slice(1, part.length - 1)

					if (!request.params || !request.params.has(param)) {
						throw new Error(`Missing request parameter: ${param}`)
					}

					routeUrlParts.push(request.params.get(param))
				} else {
					routeUrlParts.push(part)
				}
			})

			route = routeUrlParts.join('/')
		}

		if (request && request.q) {
			const qParts: Array<string> = []
			const sortedQ = new Map([...request.q.entries()].sort())

			sortedQ.forEach((qValue: string, qKey: string) => {
				qParts.push(`${qKey}=${qValue}`)
			})

			route += '?' + qParts.join('&')
		}

		return import.meta.env.VITE_API_V2_URL + '/' + route
	}

	makeRoutes(routeKeys: any): Map<string, string> {
		const routes: Map<string, string> = new Map<string, string>()
		let childRoutes: Map<string, string> = new Map<string, string>()

		Object.keys(routeKeys).forEach((routeKey: string) => {
			if (typeof routeKeys[routeKey] !== 'object') {
				routes.set(routeKey, routeKeys[routeKey])
			} else {
				childRoutes = this.makeRoutes(routeKeys[routeKey])

				childRoutes.forEach((value: string, childRouteKey: string) => {
					routes.set(`${routeKey}.${childRouteKey}`, value)
				})
			}
		})

		return routes
	}
}
